import React, { useMemo, useState } from 'react';
import { Bar, BarChart, CartesianGrid, Cell, Legend, Line, LineChart, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { BarChart3Icon } from 'lucide-react';
import { useData } from '../../store/DataContext';
import { serviceSeries } from '../../data/receptionServices';
import { StatsRange, collectServiceEvents, dailyCounts, monthlyCounts, totalsInRange } from '../../utils/receptionStats';
const rangeOptions: Array<{
  value: StatsRange;
  label: string;
}> = [{
  value: 'today',
  label: 'Today'
}, {
  value: 'week',
  label: 'This Week'
}, {
  value: 'month',
  label: 'This Month'
}];
const axisProps = {
  tick: {
    fill: '#6B7280',
    fontSize: 12
  },
  axisLine: false,
  tickLine: false
} as const;
const tooltipStyle = {
  borderRadius: 12,
  border: '1px solid #E5E7EB',
  boxShadow: '0 8px 24px rgba(17,24,39,0.08)',
  fontSize: 12
};
export function ReceptionServiceCharts() {
  const {
    patients,
    encounters,
    maternityFiles
  } = useData();
  const [range, setRange] = useState<StatsRange>('week');
  const events = useMemo(() => collectServiceEvents(patients, encounters, maternityFiles), [patients, encounters, maternityFiles]);
  const daily = useMemo(() => dailyCounts(events, range), [events, range]);
  const monthly = useMemo(() => monthlyCounts(events, 6), [events]);
  const totals = useMemo(() => totalsInRange(events, range), [events, range]);
  const pieData = serviceSeries.map(s => ({
    name: s.label,
    value: totals[s.key],
    color: s.color
  })).filter(d => d.value > 0);
  const totalServices = pieData.reduce((sum, d) => sum + d.value, 0);
  const rangeLabel = rangeOptions.find(o => o.value === range)?.label;
  return <section aria-labelledby="service-activity-heading" className="space-y-4" data-id="element-10310" data-mp="1vjr50cbhwvds">
      <div className="flex flex-wrap items-center justify-between gap-3" data-id="element-10311" data-mp="1qm7rjemmgmmw">
        <h2 id="service-activity-heading" className="text-lg font-bold text-gray-900" data-id="element-10312" data-mp="1lned9jzigrlo">
          Service activity
        </h2>
        <div role="group" aria-label="Date range" className="inline-flex rounded-full border border-gray-200 bg-white p-1 shadow-sm" data-id="element-10313" data-mp="1c2er54fyf46j">
          {rangeOptions.map(option => <button key={option.value} type="button" aria-pressed={range === option.value} onClick={() => setRange(option.value)} className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors duration-150 ${range === option.value ? 'bg-chrome-active text-white' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-10314" data-mp="170pexlgzwkgv">
              {option.label}
            </button>)}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3" data-id="element-10315" data-mp="2dpe6xd4oxbht">
        {/* Bar — services compared by day (primary) */}
        <article className="rounded-xl bg-white p-4 shadow-sm xl:col-span-2" data-id="element-10316" data-mp="2ignv09alc6rl">
          <header className="mb-2" data-id="element-10317" data-mp="715hlx7tnnod">
            <h3 className="text-sm font-semibold text-gray-900" data-id="element-10318" data-mp="703siotz10nn">
              Services by day
            </h3>
            <p className="text-xs text-gray-500" data-id="element-10319" data-mp="219avq7qpi54">
              Daily counts per service · {rangeLabel}
            </p>
          </header>
          <div className="h-52" data-id="element-10320" data-mp="gsroxih1h2lu">
            <ResponsiveContainer width="100%" height="100%" data-id="element-10321" data-mp="brc51tqll251">
              <BarChart data={daily} barGap={2} data-id="element-10322" data-mp="bu80nbez3eqb">
                <CartesianGrid strokeDasharray="4 4" vertical={false} stroke="#EEF0F4" data-id="element-10323" data-mp="3k8qdrrjrtkgs" />
                <XAxis dataKey="label" {...axisProps} interval="preserveStartEnd" data-id="element-10324" data-mp="3f9ds3a4myjfs" />
                <YAxis allowDecimals={false} {...axisProps} width={32} data-id="element-10325" data-mp="3t83rwxiuax7u" />
                <Tooltip contentStyle={tooltipStyle} cursor={{
                fill: '#F5F6FA'
              }} data-id="element-10326" data-mp="3t60pfe0i3bm8" />
                <Legend iconType="circle" wrapperStyle={{
                fontSize: 12
              }} data-id="element-10327" data-mp="3p5gu8fzhmi2q" />
                {serviceSeries.map(s => <Bar key={s.key} dataKey={s.key} name={s.label} fill={s.color} radius={[4, 4, 0, 0]} maxBarSize={14} data-id="element-10328" data-mp="18jz3pz1bya7q" />)}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </article>

        {/* Pie — service mix for the range */}
        <article className="flex flex-col rounded-xl bg-white p-4 shadow-sm" data-id="element-10329" data-mp="2dq7fxsnu5t9g">
          <header className="mb-1" data-id="element-10330" data-mp="2do4fsfsyyjr0">
            <h3 className="text-sm font-semibold text-gray-900" data-id="element-10331" data-mp="2iqdbhjx87zwt">
              Service mix
            </h3>
            <p className="text-xs text-gray-500" data-id="element-10332" data-mp="2mq3w15w5tjsw">{rangeLabel}</p>
          </header>
          {totalServices === 0 ? <div className="flex flex-1 flex-col items-center justify-center py-10 text-center text-gray-500" data-id="element-10333" data-mp="2rif97afwv23e">
              <BarChart3Icon className="mb-2 h-8 w-8 text-gray-300" data-id="element-10334" data-mp="2wg6xzvl9e83d" />
              <p className="text-sm" data-id="element-10335" data-mp="31h4f04uj8w3a">No services recorded for this period.</p>
            </div> : <>
              <div className="relative h-36" data-id="element-10336" data-mp="36feakgk65ypf">
                <ResponsiveContainer width="100%" height="100%" data-id="element-10337" data-mp="usc7d743qhva">
                  <PieChart data-id="element-10338" data-mp="upg8ngkfjp5s">
                    <Pie data={pieData} dataKey="value" nameKey="name" innerRadius="58%" outerRadius="90%" paddingAngle={2} stroke="none" data-id="element-10339" data-mp="ps6nid42pznr">
                      {pieData.map(d => <Cell key={d.name} fill={d.color} data-id="element-10340" data-mp="1dkdcusbdt6do" />)}
                    </Pie>
                    <Tooltip contentStyle={tooltipStyle} data-id="element-10341" data-mp="18ljzczw3hfdi" />
                  </PieChart>
                </ResponsiveContainer>
                <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center" data-id="element-10342" data-mp="yto37udkjzbx">
                  <span className="text-lg font-bold text-gray-900" data-id="element-10343" data-mp="1w91v16ihcxlr">
                    {totalServices}
                  </span>
                  <span className="text-xs text-gray-500" data-id="element-10344" data-mp="1rgijsg79mek4">services</span>
                </div>
              </div>
              <ul className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs" data-id="element-10345" data-mp="3lqp8u6138z0l">
                {serviceSeries.map(s => <li key={s.key} className="flex items-center gap-2" data-id="element-10346" data-mp="3c042txj34s7n">
                    <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{
                backgroundColor: s.color
              }} aria-hidden="true" data-id="element-10347" data-mp="3bzufkhpfcz5n" />
                    <span className="truncate text-gray-600" data-id="element-10348" data-mp="331sey39ls8iw">{s.label}</span>
                    <span className="ml-auto font-semibold tabular-nums text-gray-900" data-id="element-10349" data-mp="372ccv9203g63">
                      {totals[s.key]}
                    </span>
                  </li>)}
              </ul>
            </>}
        </article>
      </div>

      {/* Line — monthly trend */}
      <article className="rounded-xl bg-white p-4 shadow-sm" data-id="element-10350" data-mp="raogbeqwfpnt">
        <header className="mb-2" data-id="element-10351" data-mp="w9829f5ht1g6">
          <h3 className="text-sm font-semibold text-gray-900" data-id="element-10352" data-mp="w86rzcwawizi">
            Monthly trend
          </h3>
          <p className="text-xs text-gray-500" data-id="element-10353" data-mp="2gradlu9q2cdj">
            Services per month · last 6 months
          </p>
        </header>
        <div className="h-52" data-id="element-10354" data-mp="2qgtuwvhsdy6a">
          <ResponsiveContainer width="100%" height="100%" data-id="element-10355" data-mp="2qh2aiv5anhl6">
            <LineChart data={monthly} data-id="element-10356" data-mp="2qimzm58ua8bn">
              <CartesianGrid strokeDasharray="4 4" vertical={false} stroke="#EEF0F4" data-id="element-10357" data-mp="2lp2dbaglz2sa" />
              <XAxis dataKey="label" {...axisProps} data-id="element-10358" data-mp="2zgyysvnruefp" />
              <YAxis allowDecimals={false} {...axisProps} width={32} data-id="element-10359" data-mp="2zjl93321r81m" />
              <Tooltip contentStyle={tooltipStyle} data-id="element-10360" data-mp="2uhm4xqyhg73m" />
              <Legend iconType="circle" wrapperStyle={{
              fontSize: 12
            }} data-id="element-10361" data-mp="39foc0b4q3apm" />
              {serviceSeries.map(s => <Line key={s.key} type="monotone" dataKey={s.key} name={s.label} stroke={s.color} strokeWidth={2.5} dot={false} activeDot={{
              r: 5
            }} data-id="element-10362" data-mp="34g1sxl27r08w" />)}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </article>
    </section>;
}